import React, { useState, useEffect, useRef } from "react";
import { Match } from "../types";
import { getThreadsUrl } from "../utils/threads";
import { useFirestore } from "../contexts/FirestoreContext";

const PrayerLibrary: React.FC = () => {
  const { prayers, matches: matchList, loadMorePrayers, hasMorePrayers, loadingMore, totalPrayerCount, searchResults, searchPrayers, clearSearch } = useFirestore();

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const isSearching = searchResults !== null;
  const displayPrayers = searchResults ?? prayers;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollRef.current;
    if (!sentinel || !scrollContainer || isSearching) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMorePrayers && !loadingMore) {
          loadMorePrayers();
        }
      },
      { root: scrollContainer, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMorePrayers, loadingMore, loadMorePrayers, isSearching]);

  // Build match lookup map
  const matches: Record<string, Match> = {};
  matchList.forEach((m) => {
    matches[m.id] = m;
  });

  const getTimeAgo = (timestamp: number): string => {
    if (!timestamp || isNaN(timestamp)) return "";
    const diff = Date.now() - timestamp;
    if (diff < 0) return "Vừa xong"; // future timestamp (client clock skew)
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Vừa xong";
    if (minutes < 60) return `${minutes}p trước`;
    if (hours < 24) return `${hours}h trước`;
    return `${days}d trước`;
  };

  // Animated prayer counter
  const [displayCount, setDisplayCount] = useState(0);
  const prevCountRef = useRef(0);

  useEffect(() => {
    const target = totalPrayerCount;
    const start = prevCountRef.current;
    if (target === start) return;

    const duration = 1200;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + (target - start) * eased);
      setDisplayCount(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        prevCountRef.current = target;
      }
    };

    requestAnimationFrame(animate);
  }, [totalPrayerCount]);

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && searchTerm.trim()) {
      searchPrayers(searchTerm);
    }
  };

  const handleChange = (value: string) => {
    setSearchTerm(value);
    if (!value.trim()) {
      clearSearch();
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    clearSearch();
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-12 h-0.5 bg-primary mx-auto mb-6" />
        <h2 className="font-heading text-4xl md:text-5xl font-bold mb-3">
          <span className="text-primary">BẢNG</span>{" "}
          <span className="text-white">LỜI KHẤN</span>
        </h2>
        <p className="text-white/30 text-sm font-body tracking-wide">
          {totalPrayerCount > 0
            ? <><span className="text-primary font-heading font-bold text-2xl">{displayCount}</span> lời khấn từ Tê con</>
            : "Hãy là người đầu tiên thắp hương!"}
        </p>
        <div className="w-12 h-0.5 bg-primary mx-auto mt-6" />
      </div>

      {/* Search bar */}
      <div className="relative max-w-sm mx-auto mb-6">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm font-bold font-heading">@</span>
        <input
          type="text"
          placeholder="Tìm theo tên Threads... (Enter)"
          value={searchTerm}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleSearch}
          className="w-full pl-9 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/25 focus:border-primary/60 focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all font-body text-sm"
        />
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition cursor-pointer"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {/* Search results indicator */}
      {isSearching && (
        <p className="text-white/30 text-xs font-body text-center mb-4">
          {searchResults.length} kết quả cho <span className="text-primary">@{searchTerm.replace(/^@/, "")}</span>
        </p>
      )}

      {displayPrayers.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-6xl mb-4 opacity-10">{isSearching ? "🔍" : "🕯️"}</div>
          <p className="text-white/20 font-body">
            {isSearching ? "Không tìm thấy lời khấn nào" : "Chưa có lời khấn nào"}
          </p>
        </div>
      ) : (
        <div ref={scrollRef} className="max-h-[55vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayPrayers.map((prayer, i) => (
              <div
                key={prayer.id}
                className="prayer-card prayer-card-enter glass-card rounded-xl p-5 group"
                style={{ animationDelay: `${(i % 9) * 0.08}s` }}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <a
                    href={getThreadsUrl(prayer.threadsUsername)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-red-400 font-semibold transition text-sm inline-flex items-center gap-0.5"
                  >
                    @{prayer.threadsUsername}
                  </a>
                  <span className="text-white/40 text-[11px] font-body whitespace-nowrap ml-2">
                    {getTimeAgo(prayer.createdAt)}
                  </span>
                </div>

                {/* Match tag - uses saved label, falls back to live data */}
                {(prayer.matchLabel ||
                  (prayer.matchId && matches[prayer.matchId])) && (
                  <div className="text-[10px] text-primary/80 mb-3 border border-primary/25 rounded-md px-2 py-1 inline-block font-heading uppercase tracking-wider bg-primary/5">
                    {prayer.matchLabel ||
                      `T1 vs ${matches[prayer.matchId].opponent}`}
                  </div>
                )}

                {/* Prayer text */}
                <p className="text-white/90 text-sm leading-relaxed font-body break-words">
                  {prayer.prayerContent}
                </p>
              </div>
            ))}
          </div>

          {/* Infinite scroll sentinel - only when not searching */}
          {!isSearching && (
            <>
              <div ref={sentinelRef} className="h-4" />
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <span className="text-white/30 text-sm font-body">Đang tải thêm...</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PrayerLibrary;
